/**
 * useIssuesFilters — clientseitige Filter/Sort/Suche ueber aggregierte
 * Produkt-Issues. Bewusst clientseitig: Backend liefert workspace-weite
 * Aggregation (gebounded), Facetten/Suche/Sort sind interaktiv.
 */
import { useMemo, useState } from 'react'
import type { ProductWithIssues } from '@/lib/api/channel-readiness'
import { tally, sortProducts, toggleValue, type IssuesSort } from './useIssuesFilters.helpers'

export type { IssuesSort }

export function useIssuesFilters(products: ProductWithIssues[]) {
  const [types, setTypes] = useState<Set<string>>(new Set())
  const [severities, setSeverities] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<IssuesSort>('count_desc')

  const typeCounts = useMemo(() => tally(products, 'issue_type'), [products])
  const severityCounts = useMemo(() => tally(products, 'severity'), [products])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const out = products.filter((p) => {
      if (types.size && !p.issues.some((i) => types.has(i.issue_type))) return false
      if (severities.size && !p.issues.some((i) => severities.has(i.severity ?? 'info'))) return false
      if (q && !`${p.name ?? ''} ${p.sku ?? ''}`.toLowerCase().includes(q)) return false
      return true
    })
    return sortProducts(out, sort)
  }, [products, types, severities, search, sort])

  return {
    filtered,
    typeCounts,
    severityCounts,
    activeTypes: types,
    activeSeverities: severities,
    search,
    sort,
    setSearch,
    setSort,
    toggleType: toggleValue(types, setTypes),
    toggleSeverity: toggleValue(severities, setSeverities),
  }
}
